import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  getConsentStatus,
  setConsentStatus,
  isDntEnabled,
  isDntBannerDismissed,
  dismissDntBanner,
  resetDntBanner,
} from '../lib/consent';

export default function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [dnt, setDnt] = useState(false);

  useEffect(() => {
    const hasDnt = isDntEnabled();
    setDnt(hasDnt);

    if (hasDnt) {
      // DNT enabled: show info banner unless already dismissed
      if (!isDntBannerDismissed()) {
        setVisible(true);
      }
    } else {
      // Normal flow: show banner only if user hasn't made a choice
      if (getConsentStatus() === 'undecided') {
        setVisible(true);
      }
    }

    // Listen for consent resets (Cookie Preferences link)
    const handleReset = () => {
      if (isDntEnabled()) {
        resetDntBanner();
        setDnt(true);
        setVisible(true);
      } else {
        setVisible(true);
      }
    };

    window.addEventListener('plevits:consent-change', handleReset);
    return () =>
      window.removeEventListener('plevits:consent-change', handleReset);
  }, []);

  if (!visible) return null;

  // DNT variant: informational only
  if (dnt) {
    return (
      <div className="fixed bottom-0 inset-x-0 z-50 p-4">
        <div className="max-w-6xl mx-auto bg-white rounded-lg shadow-lg border border-gray-200 p-4 sm:flex sm:items-center sm:justify-between gap-4">
          <p className="text-sm text-gray-600">
            Your browser&apos;s Do Not Track setting is enabled. We respect this
            and analytics cookies are permanently disabled.{' '}
            <Link
              to="/cookies"
              className="text-primary-600 hover:underline"
            >
              Learn more
            </Link>
          </p>
          <div className="flex items-center gap-2 mt-3 sm:mt-0 shrink-0">
            <button
              onClick={() => {
                dismissDntBanner();
                setVisible(false);
              }}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors"
            >
              Got it
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Normal consent banner
  return (
    <div className="fixed bottom-0 inset-x-0 z-50 p-4">
      <div className="max-w-6xl mx-auto bg-white rounded-lg shadow-lg border border-gray-200 p-4 sm:flex sm:items-center sm:justify-between gap-4">
        <p className="text-sm text-gray-600">
          We use cookies to understand how you use Plevits and improve your
          experience.{' '}
          <Link to="/cookies" className="text-primary-600 hover:underline">
            Learn more
          </Link>
        </p>
        <div className="flex items-center gap-2 mt-3 sm:mt-0 shrink-0">
          <button
            onClick={() => {
              setConsentStatus('denied');
              setVisible(false);
            }}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
          >
            Decline
          </button>
          <button
            onClick={() => {
              setConsentStatus('granted');
              setVisible(false);
            }}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}
